/**
 * Phase 735.1 — Agenda variable resolver (frontend).
 *
 * Mirrors the backend helper `_build_meeting_vars` / `_apply_meeting_vars`
 * inside `server.py` so that the same placeholders resolve consistently
 * whether the text is shown in the browser or exported to a PDF.
 *
 * Supported placeholders (identical to the Agenda Templates editor):
 *   Company    — {company_name} {company_email} {company_phone}
 *   Building   — {building_name}
 *   Meeting    — {meeting_title} {meeting_date} {meeting_start_time}
 *                {meeting_end_time} {meeting_location}
 *   Chairman   — {chairman_name} {chairman_apartment} {chairman_phone}
 *   Secretary  — {secretary_name} {secretary_apartment} {secretary_phone}
 *   Treasurer  — {treasurer_name} {treasurer_apartment} {treasurer_phone}
 *
 * The `president` role is treated as an alias for `chairman` (matches the
 * backend + the app's existing committee data shape).
 */

// Backend + frontend BOTH format the meeting date as DD/MM/YYYY.
function formatDateDMY(iso) {
  if (!iso) return '';
  const p = String(iso).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso);
}

/**
 * Build the {token → value} map for a single meeting.
 *
 * @param {object} meeting  A meeting doc OR the in-progress form state. Any
 *                          field that is missing simply resolves to "".
 * @param {Array}  apartments  The building's apartments list (needed to look
 *                             up committee owner_name / apartment_number / phone).
 * @param {string} buildingName  Fallback building name (usually `buildingName`
 *                               prop in the page that owns the meeting).
 * @param {object} companySettings  Company Settings doc (from GET /company-settings).
 */
export function buildAgendaVars(meeting, apartments = [], buildingName = '', companySettings = null) {
  const cs = companySettings || {};
  const m = meeting || {};

  const roleAlias = { president: 'chairman', chairman: 'chairman', secretary: 'secretary', treasurer: 'treasurer' };
  const resolved = { chairman: {}, secretary: {}, treasurer: {} };

  const committee = m.committee || [];
  for (const member of committee) {
    const roleKey = roleAlias[(member?.role || '').toLowerCase()];
    if (!roleKey) continue;
    if (member.active === false) continue;
    let name = '';
    let apt = '';
    let phone = '';
    if (member.apartment_id) {
      const aptDoc = (apartments || []).find((a) => a.apartment_id === member.apartment_id);
      if (aptDoc) {
        name = (aptDoc.owner_name || '').trim();
        apt = String(aptDoc.apartment_number || '');
        phone = (aptDoc.owner_phone || aptDoc.phone || '').trim();
      }
    }
    if (!name) name = (member.name || member.custom_name || '').trim();
    if (!resolved[roleKey]?.name) {
      resolved[roleKey] = { name, apartment: apt, phone };
    }
  }

  const pick = (role, field) => (resolved[role] && resolved[role][field]) || '';

  return {
    '{company_name}': cs.company_name || '',
    '{company_email}': cs.company_email || '',
    '{company_phone}': cs.company_phone || '',
    '{building_name}': buildingName || m.building_name || '',
    '{meeting_title}': (m.title || '').trim(),
    '{meeting_date}': formatDateDMY(m.date),
    '{meeting_start_time}': (m.time || '').trim(),
    '{meeting_end_time}': (m.end_time || '').trim(),
    '{meeting_location}': (m.location || '').trim(),
    '{chairman_name}': pick('chairman', 'name'),
    '{chairman_apartment}': pick('chairman', 'apartment'),
    '{chairman_phone}': pick('chairman', 'phone'),
    '{secretary_name}': pick('secretary', 'name'),
    '{secretary_apartment}': pick('secretary', 'apartment'),
    '{secretary_phone}': pick('secretary', 'phone'),
    '{treasurer_name}': pick('treasurer', 'name'),
    '{treasurer_apartment}': pick('treasurer', 'apartment'),
    '{treasurer_phone}': pick('treasurer', 'phone'),
  };
}

/**
 * Replace every `{token}` in `text` with its value in `vars`. Tokens that
 * exist in the map but have an empty value are replaced with an empty
 * string (never left as a literal placeholder). Tokens NOT in the map are
 * likewise stripped so no `{whatever}` ever reaches the user.
 */
export function resolveAgendaVarsInText(text, vars) {
  if (!text) return '';
  const map = vars || {};
  return String(text).replace(/\{[a-z_]+\}/gi, (m) => (m in map ? map[m] : ''));
}

/**
 * Report which tokens inside `text` cannot be resolved (missing or empty
 * value in `vars`). Used by callers that want to surface a warning to the
 * user when the meeting is incomplete.
 */
export function listUnresolvedAgendaVars(text, vars) {
  if (!text) return [];
  const map = vars || {};
  const found = String(text).match(/\{[a-z_]+\}/gi) || [];
  const missing = new Set();
  for (const tok of found) {
    const val = map[tok];
    if (val === undefined || val === null || String(val).trim() === '') {
      missing.add(tok);
    }
  }
  return Array.from(missing);
}
